<template>
  <div class="gs-app gs-refresh-controls gm-admin-page">
    <header class="gs-wizard-card gm-admin-page__header">
      <div class="gm-admin-page__heading">
        <p class="gm-admin-page__eyebrow">
          {{ eyebrow || $t('frontend_groovemail_components_sidebar_admin') }}
        </p>
        <h1 class="gm-admin-page__title">{{ title }}</h1>
        <p v-if="subtitle" class="gm-admin-page__subtitle">{{ subtitle }}</p>
      </div>
      <div v-if="$slots.actions" class="gm-admin-page__actions">
        <slot name="actions" />
      </div>
    </header>

    <section class="gs-wizard-card gm-admin-page__content">
      <div v-if="$slots.tabs" class="gm-admin-page__tabs">
        <slot name="tabs" />
      </div>
      <div class="gm-admin-page__body">
        <slot />
      </div>
    </section>
  </div>
</template>

<script>
/**
 * Presentational shell for GrooveMail admin pages: header card
 * (eyebrow → h1 → subtitle, optional right-aligned actions) plus a
 * content card that hosts an optional tab strip and the page body.
 * It owns no behaviour; pages keep their own data, methods and modals.
 */
export default {
  name: 'AdminPageShell',
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: '',
    },
    eyebrow: {
      type: String,
      default: '',
    },
  },
};
</script>

<style>
/* ---------------------------------------------------------------------
 * Shell
 * ------------------------------------------------------------------- */
.gm-admin-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
  color: var(--gs-foreground);
}

.gm-admin-page .gs-wizard-card {
  padding: 20px 24px;
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gm-admin-page__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.gm-admin-page__heading {
  min-width: 0;
}

.gm-admin-page__eyebrow {
  margin: 0 0 4px;
  color: var(--gs-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.gm-admin-page__title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}

.gm-admin-page__subtitle {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.5;
}

.gm-admin-page__actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 10px;
}

.gm-admin-page__content {
  min-width: 0;
}

.gm-admin-page__tabs {
  margin: -4px 0 20px;
}

.gm-admin-page__tabs .tabs__item .leads-field {
  margin: 0;
  font-size: inherit;
}

/* Design-system inline tabs (DESIGN.md §3.9) for the GdTabs instance hosted in
   the admin shell only; other GdTabs consumers keep their legacy styling. */
.gm-admin-page__tabs .tabs {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0 12px;
  margin: 0;
  padding: 0;
  border-bottom: 1px solid var(--gs-border);
}

.gm-admin-page__tabs .tabs .tabs__item {
  position: relative;
  margin: 0;
  padding: 10px 2px 12px;
  border: 0;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  background: transparent;
  color: var(--gs-muted-foreground);
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  transition: color 150ms ease;
}

.gm-admin-page__tabs .tabs .tabs__item:hover:not(:disabled) {
  color: var(--gs-foreground);
}

.gm-admin-page__tabs .tabs .tabs__item:focus {
  outline: none;
}

.gm-admin-page__tabs .tabs .tabs__item:focus-visible {
  border-radius: var(--gs-radius-sm);
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gm-admin-page__tabs .tabs .tabs__item:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.gm-admin-page__tabs .tabs .tabs__item.text-grey-300 {
  color: var(--gs-border-strong);
}

.gm-admin-page__tabs .tabs .tabs__item.tabs__item_active {
  color: var(--gs-primary);
}

.gm-admin-page__tabs .tabs .tabs__item.tabHeadingWithError,
.gm-admin-page__tabs .tabs .tabs__item.tabHeadingWithError .text-red {
  color: var(--gs-danger);
}

.gm-admin-page__tabs .tabs .tabs__active-line {
  position: absolute;
  bottom: -1px;
  left: 0;
  height: 2px;
  margin: 0;
  background: var(--gs-primary);
  border-radius: 999px;
  pointer-events: none;
  transition: transform 200ms ease, width 200ms ease;
}

@media (prefers-reduced-motion: reduce) {
  .gm-admin-page__tabs .tabs .tabs__item,
  .gm-admin-page__tabs .tabs .tabs__active-line {
    transition: none;
  }
}

.gm-admin-page__body {
  min-width: 0;
}

/* ---------------------------------------------------------------------
 * Tab / section content
 * ------------------------------------------------------------------- */
.gm-admin-tab {
  min-width: 0;
}

.gm-admin-tab__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.gm-admin-tab__head > div {
  min-width: 0;
}

.gm-admin-tab h2,
.gm-admin-page__body > h2 {
  margin: 0 0 4px;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.gm-admin-tab__hint,
.gm-admin-page__hint {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

.gm-admin-tab__section {
  padding: 18px 0;
  border-top: 1px solid var(--gs-border);
}

.gm-admin-tab__section:first-of-type {
  border-top: 0;
  padding-top: 0;
}

.gm-admin-tab__section h3 {
  margin: 0 0 4px;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.35;
}

.gm-admin-tab__section > .gm-admin-tab__hint {
  margin-bottom: 14px;
}

.gm-admin-tab__row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px 20px;
}

.gm-admin-tab__row .gs-field {
  flex: 0 1 260px;
  min-width: 200px;
}

.gm-admin-tab__row .gs-field--grow {
  flex: 1 1 320px;
}

.gm-admin-tab__stack {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.gm-admin-tab__footer,
.gm-admin-page__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid var(--gs-border);
}

.gm-admin-tab__note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

.gm-admin-tab__note svg,
.gm-admin-tab__note .fa {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  margin-top: 1px;
  color: var(--gs-muted-foreground);
}

.gm-admin-tab__error {
  margin: 6px 0 0;
  color: var(--gs-danger);
  font-size: 12px;
}

.gm-admin-tab__toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gm-admin-tab__toggle-row + .gm-admin-tab__toggle-row {
  margin-top: 12px;
}

.gm-admin-tab__toggle-row > span {
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
}

.gm-admin-page__tabs .tabs__item > span {
  padding: 0;
  font-size: inherit;
  font-weight: inherit;
}

.gm-admin-form {
  max-width: 640px;
}

.gm-admin-tab__row--inline {
  align-items: flex-end;
}

.gm-admin-tab__inline-action {
  flex: 0 0 auto;
}

.gm-admin-tab__suffix {
  display: flex;
  align-items: center;
  gap: 10px;
}

.gm-admin-tab__suffix > .relative,
.gm-admin-tab__suffix > .w-full {
  flex: 1 1 auto;
}

.gm-admin-tab__suffix > span {
  flex: 0 0 auto;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  font-weight: 700;
}

.gm-admin-tab__note--spaced {
  margin: 16px 0;
}

.gm-admin-tab__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 0;
}

.gm-admin-tab__editor {
  min-width: 0;
}

.gm-admin-tab__listing {
  min-width: 0;
}

.gm-admin-tab__stack--spaced {
  margin-top: 20px;
}

.gm-admin-modal__section-title--spaced {
  margin-top: 18px;
}

/* ---------------------------------------------------------------------
 * Fields (gs-field wrapping legacy text-input / text-area / select)
 * ------------------------------------------------------------------- */
.gm-admin-page .h-10.rounded-lg.border.flex.items-center,
.gm-admin-page .gs-field > .gs-field__control > .w-full.relative {
  height: auto;
  min-height: 40px;
  border: 0;
  border-radius: var(--gs-radius-sm);
  background: transparent;
}

.gm-admin-page .gs-field textarea {
  min-height: 96px;
  padding: 10px 12px;
  line-height: 1.5;
}

.gm-admin-page .gs-field select {
  width: 100%;
  padding: 0 32px 0 12px;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23667085' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M6 9l6 6 6-6'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  font-size: 14px;
}

.gm-admin-page .gs-field .text-danger {
  position: static;
  display: block;
  margin-top: 6px;
  color: var(--gs-danger);
  font-size: 12px;
}

.gm-admin-page .gs-field .border-danger input,
.gm-admin-page .gs-field textarea.border-danger {
  border-color: var(--gs-danger);
}

.gm-admin-page .gs-field .mb-3 {
  margin-bottom: 0;
}

.gm-admin-page .gs-field__control > .gd-toggle-switch,
.gm-admin-page .gs-field__control > .flex.items-center {
  min-height: 40px;
}

/* ---------------------------------------------------------------------
 * Tables (vue-tables-2 inside gd-ui-kit ServerTable / ClientTable)
 * ------------------------------------------------------------------- */
.gm-admin-page #gd-server-table-view,
.gm-admin-page #gd-client-table-view {
  margin-top: 0;
  padding-bottom: 56px;
}

.gm-admin-page #gd-server-table-view .new-add-btn-wrapper {
  position: static;
  margin: 0 0 14px;
  overflow: visible;
}

.gm-admin-page #gd-server-table-view .new-add-btn-wrapper p.font-bold {
  display: none;
}

.gm-admin-page #gd-server-table-view .new-add-btn-wrapper > .flex > .sm\:w-1\/3 {
  display: none;
}

.gm-admin-page #gd-server-table-view .new-add-btn-wrapper > .flex > .flex {
  width: 100%;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.gm-admin-page #gd-server-table-view .new-add-btn-wrapper > .flex > .flex > * {
  margin: 0;
}

.gm-admin-page #gd-server-table-view > .flex.left-0 {
  position: static;
  margin-bottom: 14px;
}

.gm-admin-page #gd-server-table-view > .flex.left-0 label.text-lg {
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 700;
}

.gm-admin-page #gd-server-table-view .new-add-btn,
.gm-admin-page #gd-server-table-view .upload-btn {
  width: auto;
  min-width: 0;
  height: 40px;
  padding: 0 16px;
  border: 1px solid transparent;
  border-radius: var(--gs-radius-md);
  font-size: 14px;
  font-weight: 700;
  line-height: 38px;
  text-transform: none;
  box-shadow: none;
}

.gm-admin-page #gd-server-table-view .new-add-btn {
  color: var(--gs-primary-foreground);
  background: var(--gs-primary);
  border-color: var(--gs-primary);
  box-shadow: var(--gs-primary-shadow);
}

.gm-admin-page #gd-server-table-view .new-add-btn + form .upload-btn,
.gm-admin-page #gd-server-table-view .upload-btn,
.gm-admin-page #gd-server-table-view .upload-btn:after,
.gm-admin-page #gd-server-table-view .new-add-btn.ml-2 {
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border-color: var(--gs-border-strong);
  box-shadow: none;
}

.gm-admin-page #gd-server-table-view .upload-btn:after {
  width: 100%;
  line-height: 38px;
  border-radius: var(--gs-radius-md);
}

.gm-admin-page #gd-server-table-view .upload-btn:hover,
.gm-admin-page #gd-server-table-view .new-add-btn.ml-2:hover {
  background: var(--gs-muted);
}

.gm-admin-page #gd-server-table-view .bulk-action > .border,
.gm-admin-page #gd-server-table-view .bulk-action.rounded-lg {
  width: auto;
  min-width: 0;
  color: var(--gs-foreground);
  border-color: var(--gs-border-strong);
  border-radius: var(--gs-radius-md);
  font-weight: 600;
}

.gm-admin-page #gd-server-table-view .VueTables--server .table-responsive,
.gm-admin-page #gd-client-table-view .table-responsive {
  background: transparent;
}

.gm-admin-page #gd-client-table-view .VueTables__search .VueTables__search-field .VueTables__search__input {
  height: 40px;
  padding: 0 12px;
  color: var(--gs-foreground);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
  background-color: var(--gs-input);
}

.gm-admin-page #gd-server-table-view .VueTables__table,
.gm-admin-page #gd-client-table-view .VueTables__table {
  overflow: hidden;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  background: var(--gs-surface);
  box-shadow: none;
}

.gm-admin-page #gd-server-table-view .VueTables__table th,
.gm-admin-page #gd-client-table-view .VueTables__table th {
  padding: 12px 16px;
  color: var(--gs-muted-foreground);
  background: var(--gs-surface-subtle);
  border-bottom: 1px solid var(--gs-border);
  font-size: 13px;
  font-weight: 700;
  text-transform: none;
}

.gm-admin-page #gd-server-table-view .VueTables__table td,
.gm-admin-page #gd-client-table-view .VueTables__table td {
  padding: 14px 16px;
  color: var(--gs-foreground);
  border-top: 1px solid var(--gs-border);
  font-size: 14px;
  font-weight: 500;
}

.gm-admin-page #gd-server-table-view .VueTables__table tbody tr:first-child td {
  border-top: 0;
}

.gm-admin-page #gd-server-table-view th:nth-child(1),
.gm-admin-page #gd-server-table-view td:nth-child(1) {
  padding-left: 20px;
}

.gm-admin-page #gd-server-table-view #VueTables_th--check {
  padding: 12px 16px 12px 44px;
}

.gm-admin-page #gd-server-table-view tr:nth-child(even) {
  background: transparent;
}

.gm-admin-page #gd-server-table-view tbody tr:hover {
  background: var(--gs-surface-subtle);
}

.gm-admin-page #gd-server-table-view .VueTables__sort-icon,
.gm-admin-page #gd-server-table-view .VueTables__heading {
  color: var(--gs-muted-foreground);
}

.gm-admin-page #gd-server-table-view .VueTables__child-row {
  border-color: var(--gs-border);
  border-left-color: var(--gs-primary);
}

/* Search */
.gm-admin-page #gd-server-table-view .VueTables__search {
  float: none;
  display: flex;
  justify-content: flex-end;
}

.gm-admin-page #gd-server-table-view .VueTables__search .VueTables__search-field {
  margin-bottom: 14px;
}

.gm-admin-page #gd-server-table-view .VueTables__search .VueTables__search-field .VueTables__search__input {
  height: 40px;
  width: 260px;
  margin: 0;
  padding: 0 12px 0 38px;
  color: var(--gs-foreground);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
  background-color: var(--gs-input);
}

.gm-admin-page #gd-server-table-view .VueTables__search .VueTables__search-field .VueTables__search__input:focus {
  outline: none;
  border-color: var(--gs-active-border);
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gm-admin-page #gd-server-table-view .VueTables__search .VueTables__search-field:before {
  left: 10px;
  top: 8px;
  opacity: 0.7;
}

/* Pagination + page size */
.gm-admin-page #gd-server-table-view .VuePagination__pagination li {
  padding: 0;
  margin-left: 4px;
  color: var(--gs-muted-foreground);
  font-weight: 600;
}

.gm-admin-page #gd-server-table-view .VuePagination__pagination li a,
.gm-admin-page #gd-server-table-view .VuePagination__pagination li .page-link {
  display: inline-flex;
  min-width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  color: inherit;
  border: 1px solid transparent;
  border-radius: var(--gs-radius-sm);
  background: transparent;
  box-shadow: none;
  transition: background-color 150ms ease, color 150ms ease;
}

.gm-admin-page #gd-server-table-view .VuePagination__pagination li a:hover {
  color: var(--gs-foreground);
  background: var(--gs-muted);
}

.gm-admin-page #gd-server-table-view .VuePagination__pagination li.active a,
.gm-admin-page #gd-server-table-view .VuePagination__pagination li.active .page-link {
  color: var(--gs-primary);
  background: var(--gs-primary-soft);
  border-color: var(--gs-primary);
}

.gm-admin-page #gd-server-table-view .VueTables__limit {
  bottom: -52px;
}

.gm-admin-page #gd-server-table-view .VueTables__limit .VueTables__limit-field:before {
  color: var(--gs-muted-foreground);
}

.gm-admin-page #gd-server-table-view .VueTables__limit .VueTables__limit-field select {
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
  color: var(--gs-foreground);
}

/* Row actions rendered by ServerTable slot config (script-owned):
   turn icon buttons into ghost icons, "Actions" trigger into an outline button. */
.gm-admin-page #gd-server-table-view td .icon-btn-width {
  width: 36px;
  height: 36px;
  min-width: 36px;
  padding: 0;
  color: var(--gs-muted-foreground);
  background: transparent;
  border-color: transparent;
  border-radius: var(--gs-radius-md);
  box-shadow: none;
  transition: background-color 150ms ease, color 150ms ease;
}

.gm-admin-page #gd-server-table-view td .icon-btn-width:hover:not(:disabled) {
  color: var(--gs-foreground);
  background: var(--gs-muted);
  box-shadow: none;
}

.gm-admin-page #gd-server-table-view td .icon-btn-width.bg-primary {
  color: var(--gs-primary);
}

.gm-admin-page #gd-server-table-view td .icon-btn-width:has(.fa-trash):hover:not(:disabled),
.gm-admin-page #gd-server-table-view td .icon-btn-width:has(.fa-trash-alt):hover:not(:disabled),
.gm-admin-page #gd-server-table-view td .icon-btn-width:has(.fa-ban):hover:not(:disabled) {
  color: var(--gs-danger);
  background: var(--gs-muted);
}

.gm-admin-page #gd-server-table-view td .icon-btn-width:disabled {
  opacity: 0.45;
}

.gm-admin-page #gd-server-table-view td [role="button"] > span.border {
  height: 36px;
  color: var(--gs-foreground);
  border-color: var(--gs-border-strong);
  border-radius: var(--gs-radius-md);
  font-size: 13px;
  font-weight: 600;
  transition: background-color 150ms ease;
}

.gm-admin-page #gd-server-table-view td [role="button"] > span.border:hover {
  background: var(--gs-muted);
}

.gm-admin-page #gd-server-table-view td .float-right.pr-4 {
  padding-right: 0;
}

.gm-admin-page #gd-server-table-view [slot="dropdown"],
.gm-admin-page #gd-server-table-view .absolute.z-50 > div {
  color: var(--gs-foreground);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-md);
  text-align: left;
}

.gm-admin-page #gd-server-table-view .absolute.z-50 > div > div > div,
.gm-admin-page #gd-server-table-view .absolute.z-50 > div > div.block {
  padding: 8px 12px;
  font-size: 14px;
  transition: background-color 120ms ease;
}

.gm-admin-page #gd-server-table-view .absolute.z-50 > div > div > div:hover,
.gm-admin-page #gd-server-table-view .absolute.z-50 > div > div.block:hover {
  background: var(--gs-muted);
}

/* Status pills used in slot markup */
.gm-admin-page #gd-server-table-view td .rounded-md.uppercase.border {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.gm-admin-page #gd-server-table-view td .cursor-pointer.font-bold {
  color: var(--gs-primary);
}

/* ---------------------------------------------------------------------
 * Modals (gs-named-modal bodies live inside the page but outside .gm-admin-page
 * in the DOM tree only if teleported; the adapter renders inline, so scope here.)
 * ------------------------------------------------------------------- */
.gm-admin-modal__body {
  padding: 24px 28px 32px;
}

.gm-admin-modal__body .gs-field + .gs-field,
.gm-admin-modal__body .gs-field + .gm-admin-tab__note,
.gm-admin-modal__body .gm-admin-tab__note + .gs-field {
  margin-top: 18px;
}

.gm-admin-modal__footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
}

.gm-admin-modal__well {
  padding: 16px 18px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gm-admin-modal__dl {
  display: grid;
  grid-template-columns: minmax(120px, 160px) minmax(0, 1fr);
  gap: 12px 16px;
  margin: 0;
  font-size: 14px;
}

.gm-admin-modal__dl dt {
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 700;
}

.gm-admin-modal__dl dd {
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
}

/* Simple key/value tables rendered with divs (import mapping, senders) */
.gm-admin-kv {
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  overflow: hidden;
  font-size: 14px;
}

.gm-admin-kv__head,
.gm-admin-kv__row {
  display: flex;
  gap: 12px;
  padding: 10px 14px;
}

.gm-admin-kv__head {
  color: var(--gs-muted-foreground);
  background: var(--gs-surface-subtle);
  border-bottom: 1px solid var(--gs-border);
  font-size: 13px;
  font-weight: 700;
}

.gm-admin-kv__row {
  border-top: 1px solid var(--gs-border);
  color: var(--gs-foreground);
}

.gm-admin-kv__row:first-child {
  border-top: 0;
}

.gm-admin-kv__head > *,
.gm-admin-kv__row > * {
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
}

.gm-admin-kv__row .fa-check {
  color: var(--gs-success);
}

.gm-admin-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin: 0 0 18px;
}

.gm-admin-stats > div {
  padding: 12px 14px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gm-admin-stats dt {
  margin: 0 0 2px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 700;
}

.gm-admin-stats dd {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.gm-admin-modal__section-title {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 650;
}

.gm-admin-modal__section-title + .gm-admin-kv,
.gm-admin-stats + .gm-admin-modal__section-title {
  margin-top: 0;
}

.gm-admin-modal__qa + .gm-admin-modal__qa {
  margin-top: 14px;
}

.gm-admin-modal__qa p {
  margin: 0;
  font-size: 14px;
}

.gm-admin-modal__qa p:first-child {
  margin-bottom: 4px;
  font-weight: 700;
}

.gm-admin-modal__qa p:last-child {
  color: var(--gs-muted-foreground);
}

.gm-admin-modal__qa p:last-child > span {
  color: var(--gs-foreground);
  font-weight: 700;
}

/* ---------------------------------------------------------------------
 * Responsive
 * ------------------------------------------------------------------- */
@media (max-width: 900px) {
  .gm-admin-page__header {
    flex-direction: column;
  }

  .gm-admin-tab__head {
    flex-direction: column;
  }
}

@media (max-width: 640px) {
  .gm-admin-page .gs-wizard-card {
    padding: 16px 18px;
    border-radius: var(--gs-radius-md);
  }

  .gm-admin-page #gd-server-table-view .VueTables__search .VueTables__search-field .VueTables__search__input {
    width: 100%;
  }

  .gm-admin-modal__body {
    padding: 18px 18px 24px;
  }
}
</style>
